import { useCallback, useMemo, useState } from "react";
import {
    Box,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    List,
    ListItem,
    ListItemText,
    TextField,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useStyles } from "./KeySuccessFactors.styles";
import { IKeySuccessFactorsInput } from "./KeySuccessFactorsInputInterfaces";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import { IKeySuccessFactor } from "../MillSettingsStepInterfaces";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";

const KeySuccessFactorsInput = ({
    values,
    setFieldValue,
    handleChange,
    keySuccessFactorsArray,
    editCustomKeySuccessFactorsField,
    setEditCustomKeySuccessFactorsField,
}: IKeySuccessFactorsInput) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const [sameKSFNameError, setSameKSFNameError] = useState<boolean>(false);
    const [sameKSFNameEditionError, setSameKSFNameEditionError] = useState<boolean>(false);

    const keySuccessFactorsOptions = useMemo(
        () =>
            keySuccessFactorsArray.map((factor) => {
                const defaultKeySuccessFactorTranslatedName = translateDefaultEntity(
                    i18n.language,
                    factor,
                    "name",
                    DefaultTranslationFieldsInTable.KEY_SUCCESS_FACTOR,
                );
                return {
                    code: factor.key_success_factors_id,
                    name: defaultKeySuccessFactorTranslatedName,
                };
            }),
        [i18n.language, keySuccessFactorsArray],
    );

    const handleKeySuccessFactorsCheckedChange = useCallback(
        (checked: boolean, keySuccessFactorId: number) => {
            if (checked) {
                setFieldValue("keySuccessFactorsIds", [...values.keySuccessFactorsIds, keySuccessFactorId]);
            } else {
                setFieldValue(
                    "keySuccessFactorsIds",
                    values.keySuccessFactorsIds.filter((id) => id !== keySuccessFactorId),
                );
            }
        },
        [values.keySuccessFactorsIds, setFieldValue],
    );

    const handleCustomKeySuccessFactorsCheckedChange = useCallback(
        (checked: boolean, customKeySuccessFactor: IKeySuccessFactor, index: number) => {
            const newKeySuccessFactorsList = [
                ...values.customKeySuccessFactorsList.slice(0, index),
                {
                    ...customKeySuccessFactor,
                    enabled: checked,
                },
                ...values.customKeySuccessFactorsList.slice(index + 1),
            ];
            setFieldValue("customKeySuccessFactorsList", newKeySuccessFactorsList);
            if (customKeySuccessFactor.key_success_factors_id)
                handleKeySuccessFactorsCheckedChange(checked, customKeySuccessFactor.key_success_factors_id);
        },
        [values.customKeySuccessFactorsList, setFieldValue, handleKeySuccessFactorsCheckedChange],
    );

    const handleCustomKeySuccessFactorInputChange = useCallback(() => {
        if (Boolean(values.customKeySuccessFactorsInput?.trim())) {
            const duplicatedValues = [
                ...values?.customKeySuccessFactorsList,
                ...keySuccessFactorsOptions.map((option) => ({ ...option, deleted_at: null })),
            ].findIndex((ksf) => {
                return (
                    !Boolean(ksf?.deleted_at) &&
                    ksf?.name?.toLowerCase() === values.customKeySuccessFactorsInput.trim().toLowerCase()
                );
            });

            if (duplicatedValues !== -1) {
                setSameKSFNameError(true);
                return;
            } else {
                setSameKSFNameError(false);
            }

            setFieldValue("customKeySuccessFactorsList", [
                ...values.customKeySuccessFactorsList,
                {
                    name: values.customKeySuccessFactorsInput,
                    enabled: true,
                },
            ]);

            setFieldValue("customKeySuccessFactorsInput", "");
        }
    }, [
        values.customKeySuccessFactorsList,
        values.customKeySuccessFactorsInput,
        keySuccessFactorsOptions,
        setFieldValue,
    ]);

    const handleCustomKeySuccessFactorInputEdition = useCallback(
        (index: number) => {
            if (Boolean(values.customKeySuccessFactorsEditInput?.trim())) {
                const duplicatedValues = [
                    ...values?.customKeySuccessFactorsList,
                    ...keySuccessFactorsOptions.map((option) => ({ ...option, deleted_at: null })),
                ].findIndex((ksf) => {
                    return (
                        !Boolean(ksf.deleted_at) &&
                        ksf?.name?.toLowerCase() === values.customKeySuccessFactorsEditInput.trim().toLowerCase()
                    );
                });

                if (duplicatedValues !== -1 && duplicatedValues !== index) {
                    setSameKSFNameEditionError(true);
                    return;
                } else {
                    setSameKSFNameEditionError(false);
                }

                const customKeySuccessFactorsList = [...values.customKeySuccessFactorsList];
                customKeySuccessFactorsList[index] = {
                    ...customKeySuccessFactorsList[index],
                    name: values.customKeySuccessFactorsEditInput,
                };

                setFieldValue("customKeySuccessFactorsList", customKeySuccessFactorsList);
                setEditCustomKeySuccessFactorsField(null);
            }
        },
        [
            values.customKeySuccessFactorsEditInput,
            values.customKeySuccessFactorsList,
            keySuccessFactorsOptions,
            setEditCustomKeySuccessFactorsField,
            setFieldValue,
        ],
    );

    const handleCustomKeySuccessFactorDeletion = useCallback(
        (customKeySuccessFactor: IKeySuccessFactor, index: number) => {
            if (customKeySuccessFactor.key_success_factors_id) {
                const customKeySuccessFactorsList = [...values.customKeySuccessFactorsList];
                customKeySuccessFactorsList[index] = {
                    ...customKeySuccessFactorsList[index],
                    deleted_at: new Date(),
                };

                setFieldValue("customKeySuccessFactorsList", customKeySuccessFactorsList);
            } else {
                setFieldValue(
                    "customKeySuccessFactorsList",
                    values.customKeySuccessFactorsList.filter((_, indexFilter) => indexFilter !== index),
                );
            }
        },
        [values.customKeySuccessFactorsList, setFieldValue],
    );

    const handleCustomKeySuccessFactorEditionInitiation = useCallback(
        (customKeySuccessFactor: IKeySuccessFactor, index: number) => {
            setFieldValue("customKeySuccessFactorsEditInput", customKeySuccessFactor.name);
            setEditCustomKeySuccessFactorsField(index);
        },
        [setFieldValue, setEditCustomKeySuccessFactorsField],
    );

    return (
        <>
            <div className={classes.formSubtitle}>{t("mill_setup.key_success_factors.title")}</div>
            <div className={classes.helperText}>{t("mill_setup.key_success_factors.select_all_that_apply")}</div>

            <Box sx={{ display: "flex" }}>
                <FormControl>
                    <FormGroup className={classes.checkboxList}>
                        {keySuccessFactorsOptions.map((keySuccessFactor) => (
                            <FormControlLabel
                                className={classes.checkboxListLabel}
                                key={keySuccessFactor.code}
                                control={
                                    <Checkbox
                                        className={classes.checkbox}
                                        checked={values.keySuccessFactorsIds.includes(keySuccessFactor.code)}
                                        onChange={(_, checked) =>
                                            handleKeySuccessFactorsCheckedChange(checked, keySuccessFactor.code)
                                        }
                                        name={keySuccessFactor.name}
                                    />
                                }
                                label={keySuccessFactor.name}
                            />
                        ))}
                        <FormControlLabel
                            className={classes.checkboxListLabel}
                            control={
                                <Checkbox
                                    className={classes.checkbox}
                                    checked={values.isCustomKeySuccessFactors}
                                    onChange={handleChange}
                                    name="isCustomKeySuccessFactors"
                                />
                            }
                            label={t("mill_setup.key_success_factors.other")}
                        />
                    </FormGroup>
                </FormControl>
            </Box>
            {values.isCustomKeySuccessFactors && (
                <>
                    <TextField
                        fullWidth
                        id="customKeySuccessFactorsInput-input"
                        name="customKeySuccessFactorsInput"
                        type="text"
                        variant="outlined"
                        className={classes.textField}
                        label={t("mill_setup.key_success_factors.ksf_name")}
                        value={values.customKeySuccessFactorsInput}
                        error={sameKSFNameError}
                        helperText={sameKSFNameError && t("mill_setup.error_message.existing_key_success_factor_name")}
                        onKeyDown={(event) => {
                            if (event.key === "Enter") {
                                event.preventDefault();
                                if (Boolean(values.customKeySuccessFactorsInput)) {
                                    handleCustomKeySuccessFactorInputChange();
                                }
                            }
                        }}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={() => {
                                        if (Boolean(values.customKeySuccessFactorsInput)) {
                                            handleCustomKeySuccessFactorInputChange();
                                        }
                                    }}
                                >
                                    <AddIcon />
                                </IconButton>
                            ),
                        }}
                        onChange={handleChange}
                    />
                    {Boolean(values.customKeySuccessFactorsList.length) && (
                        <List className={classes.listContainer}>
                            {values.customKeySuccessFactorsList.map((keySuccessFactor, index) => {
                                return (
                                    <ListItem
                                        style={{ ...(keySuccessFactor.deleted_at && { display: "none" }) }}
                                        key={index}
                                        className={classes.keySuccessFactorlistItem}
                                    >
                                        <Checkbox
                                            className={classes.checkbox}
                                            checked={keySuccessFactor.enabled}
                                            onChange={(_, isCustomKeySuccessFactorEnabled) =>
                                                handleCustomKeySuccessFactorsCheckedChange(
                                                    isCustomKeySuccessFactorEnabled,
                                                    keySuccessFactor,
                                                    index,
                                                )
                                            }
                                        />
                                        {editCustomKeySuccessFactorsField !== index && (
                                            <ListItemText
                                                className={classes.listText}
                                                primary={keySuccessFactor.name}
                                            />
                                        )}

                                        {editCustomKeySuccessFactorsField === index && (
                                            <TextField
                                                fullWidth
                                                label={t("mill_setup.key_success_factors.edit_name")}
                                                variant="outlined"
                                                autoFocus={true}
                                                name="customKeySuccessFactorsEditInput"
                                                onChange={handleChange}
                                                value={values.customKeySuccessFactorsEditInput}
                                                error={sameKSFNameEditionError}
                                                helperText={
                                                    sameKSFNameEditionError &&
                                                    t("mill_setup.error_message.existing_key_success_factor_name")
                                                }
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") {
                                                        event.preventDefault();
                                                        handleCustomKeySuccessFactorInputEdition(index);
                                                    }
                                                }}
                                                InputProps={{
                                                    endAdornment: (
                                                        <>
                                                            <IconButton
                                                                onClick={() =>
                                                                    handleCustomKeySuccessFactorInputEdition(index)
                                                                }
                                                            >
                                                                <CheckIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                onClick={() =>
                                                                    setEditCustomKeySuccessFactorsField(null)
                                                                }
                                                            >
                                                                <CloseIcon />
                                                            </IconButton>
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}

                                        {editCustomKeySuccessFactorsField !== index && (
                                            <>
                                                <IconButton
                                                    onClick={() =>
                                                        handleCustomKeySuccessFactorEditionInitiation(
                                                            keySuccessFactor,
                                                            index,
                                                        )
                                                    }
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                                <IconButton
                                                    onClick={() =>
                                                        handleCustomKeySuccessFactorDeletion(keySuccessFactor, index)
                                                    }
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </>
                                        )}
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </>
            )}
        </>
    );
};

export default KeySuccessFactorsInput;
